import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { DashboardActions } from './dashboard.actions';
import {
  selectBlockActionExecutions,
  selectBlockActionExecutionsLoading,
  selectDashboards,
  selectDashboardLoadError,
  selectDateRangeQuery,
  selectGlobalFilterValues,
  selectHttpResult,
  selectSelectedDashboard,
  selectSelectedDashboardId,
} from './dashboard.selectors';
import { CreateDashboardBlockDTO, IDashboardBlock } from './interfaces/dashboard-block.interface';
import { CreateDashboardDTO, DateRangeQuery, IDashboard } from './interfaces/dashboard.interface';

@Injectable()
export class DashboardService {
  private store = inject(Store);

  public get dashboards$() {
    return this.store.select(selectDashboards);
  }
  public get selectedDashboard$() {
    return this.store.select(selectSelectedDashboard);
  }
  public selectedDashboard = this.store.selectSignal(selectSelectedDashboard);
  /** Set when the dashboard that was last opened answered 403 or 404 (WB-185). */
  public loadError = this.store.selectSignal(selectDashboardLoadError);
  public get selectedDashboardId$() {
    return this.store.select(selectSelectedDashboardId);
  }

  public get dateRangeQuery$() {
    return this.store.select(selectDateRangeQuery);
  }

  public dateRangeQuery = this.store.selectSignal(selectDateRangeQuery);

  public globalFilterValues = this.store.selectSignal(selectGlobalFilterValues);

  public blockActionExecutions = this.store.selectSignal(selectBlockActionExecutions);
  public blockActionExecutionsLoading = this.store.selectSignal(selectBlockActionExecutionsLoading);

  /** Fetches the action executions of one block. Scoped to the block, not to a single action list. */
  public loadBlockActionExecutions(blockId: id): void {
    this.store.dispatch(DashboardActions.loadBlockActionExecutions({ blockId }));
  }

  /** Marks one `SEND_NOTIFICATION` execution read. `blockId` is what keys `blockActionExecutions`. */
  public markActionExecutionRead(blockId: id, executionId: number): void {
    this.store.dispatch(DashboardActions.markActionExecutionRead({ blockId, executionId }));
  }

  public get httpResultBS$() {
    return this.store.select(selectHttpResult);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private httpResultSource = new Subject<{ action: any; error?: unknown }>();
  public httpResult$ = this.httpResultSource.asObservable();

  constructor() {
    this.httpResultBS$.subscribe((result) => this.httpResultSource.next(result));
  }

  loadDashboard(dashboardId: id): void {
    this.store.dispatch(DashboardActions.loadDashboard({ dashboardId }));
  }

  loadDashboards(): void {
    this.store.dispatch(DashboardActions.loadDashboards());
  }

  createDashboard(dashboard: CreateDashboardDTO): void {
    this.store.dispatch(DashboardActions.createDashboard({ dashboard }));
  }

  updateDashboard(dashboard: IDashboard): void {
    this.store.dispatch(DashboardActions.updateDashboard({ dashboard }));
  }

  deleteDashboard(dashboardId: id): void {
    this.store.dispatch(DashboardActions.deleteDashboard({ dashboardId }));
  }

  selectDashboard(dashboardId: id): void {
    this.store.dispatch(DashboardActions.selectDashboard({ dashboardId }));
  }

  sortDashboardBlocks(dashboardId: id, blocks: IDashboardBlock[]): void {
    this.store.dispatch(DashboardActions.sortDashboardBlocks({ dashboardId, blocks }));
  }

  deleteDashboardBlock(dashboardId: id, blockId: id): void {
    this.store.dispatch(DashboardActions.deleteDashboardBlock({ dashboardId, blockId }));
  }

  updateDashboardBlock(dashboardId: id, block: IDashboardBlock): void {
    this.store.dispatch(DashboardActions.updateDashboardBlock({ dashboardId, block }));
  }

  createDashboardBlock(dashboardId: id, block: CreateDashboardBlockDTO): void {
    this.store.dispatch(DashboardActions.createDashboardBlock({ dashboardId, block }));
  }

  selectEditingBlock(id: id): void {
    this.store.dispatch(DashboardActions.selectEditingBlockId({ id }));
  }

  addChartToBlock(chartId: id): void {
    this.store.dispatch(DashboardActions.addChartToBlock({ chartId }));
  }

  updateDateRangeQuery(dateRange: DateRangeQuery | null): void {
    this.store.dispatch(DashboardActions.updateDateRangeQuery({ dateRange }));
  }

  setGlobalFilterValue(key: string, value: unknown): void {
    this.store.dispatch(DashboardActions.setGlobalFilterValue({ key, value }));
  }
}
